/* eslint-disable no-nested-ternary */
import React, { ReactElement, useMemo } from 'react';

import { Col, Divider, Layout, Row, TypographyText } from '@components/atoms';
import { noYnSurveys, requiredSurveys, requiredYnSurveys } from '@constants/enum';
import { OPTIONAL_SURVEYS_GUIDE, OPTIONAL_SURVEYS_TITLE } from '@constants/text';
import { useReduxForm } from '@hooks/useReduxForm';
import { getSurveyObj } from '@utils/surveyUtils';
import { Checkbox } from 'antd';
import _ from 'lodash';

function MainCategory(): ReactElement {
  /**
   * 어떤 설문들을 진행할 지 결정하는 화면
   * surveyList: string[] - 선택된 설문 코드를 저장하는 배열
   * */
  const { formData, setFormData } = useReduxForm('survey');
  const {
    mainSurveyList,
    selectedMainSurveyCode,
    selectedOptionalSurveyList = [],
    optionalSurveysSkipFlag,
  } = formData;

  const originSurveyList = useMemo(
    () =>
      _.uniq([
        ...(selectedMainSurveyCode ? [selectedMainSurveyCode] : []),
        ..._.filter(mainSurveyList, (k: string) => requiredSurveys.includes(k)),
      ]),
    [],
  );

  const category = useMemo(
    () => [
      { label: '해당 증상 없음', value: 0 },
      ..._.map(
        _.filter(mainSurveyList, (k: string) => !originSurveyList.includes(k)),
        (k: string, index: number) => ({
          index,
          label: getSurveyObj(k).label,
          value: k,
        }),
      ),
    ],
    [],
  );

  return (
    <Layout yzTypeLayout="SURVEY">
      <Row>
        <Col yzTypeCol="center">
          <TypographyText yzTypeTypographyText="title">{OPTIONAL_SURVEYS_TITLE}</TypographyText>
        </Col>
      </Row>
      <Divider margin={7.5} yzTypeDivider="TRANSPARENT_LITTLE_SPACE" />
      <Row>
        <Col yzTypeCol="center">
          <TypographyText yzTypeTypographyText="basic">{OPTIONAL_SURVEYS_GUIDE}</TypographyText>
        </Col>
      </Row>
      <Divider margin={8} yzTypeDivider="TRANSPARENT_LITTLE_SPACE" />
      <Row gutter={[0, 5]} style={{ rowGap: '6px', paddingInline: '5px' }}>
        {
          // eslint-disable-next-line react/no-unused-prop-types
          category.map(({ value, label }) => {
            const checked =
              value === 0 ? optionalSurveysSkipFlag : selectedOptionalSurveyList.includes(value);
            return (
              <Col
                onClick={() => {
                  if (checked) {
                    if (value === 0) {
                      const body: any = {
                        optionalSurveysSkipFlag: false,
                        selectedOptionalSurveyList: [],
                        surveyList: originSurveyList,
                      };
                      _.forEach(selectedOptionalSurveyList, (k: string) => {
                        body[k] = undefined;
                        body[`${k}Next`] = undefined;
                      });

                      setFormData(body);
                    } else {
                      const newSelectedOptionalSurveyList = _.filter(
                        selectedOptionalSurveyList,
                        (k: string) => k !== value,
                      );
                      let body;
                      if (_.isEmpty(newSelectedOptionalSurveyList)) {
                        body = {
                          [value]: undefined,
                          [`${value}Next`]: undefined,
                          selectedOptionalSurveyList: [],
                          surveyList: originSurveyList,
                        };
                      } else {
                        body = {
                          [value]: undefined,
                          [`${value}Next`]: undefined,
                          selectedOptionalSurveyList: newSelectedOptionalSurveyList,
                          surveyList: [...originSurveyList, ...newSelectedOptionalSurveyList],
                        };
                      }
                      setFormData(body);
                      // console.log(body);
                    }
                  } else if (value === 0) {
                    const body: any = {
                      optionalSurveysSkipFlag: true,
                      selectedOptionalSurveyList: [],
                      surveyList: originSurveyList,
                    };
                    _.forEach(selectedOptionalSurveyList, (k: string) => {
                      body[k] = undefined;
                      body[`${k}Next`] = undefined;
                    });

                    setFormData(body);
                  } else {
                    const newSelectedOptionalSurveyList = _.map(
                      _.sortBy(
                        category.filter((e: any) => [...selectedOptionalSurveyList, value].includes(e.value)),
                        ['index'],
                      ),
                      (e: any) => e.value,
                    );
                    const body: any = {
                      optionalSurveysSkipFlag: false,
                      selectedOptionalSurveyList: newSelectedOptionalSurveyList,
                      surveyList: [...originSurveyList, ...newSelectedOptionalSurveyList],
                    };
                    if (![...requiredYnSurveys, ...noYnSurveys].includes(value)) {
                      body[value] = 1;
                      body[`${value}Next`] = getSurveyObj(
                        newSelectedOptionalSurveyList?.find((n) => n === value),
                      )?.answers?.find((e) => e?.value)?.next;
                    }

                    setFormData(body);
                  }
                }}
                span={12}
                twoColumnsInRowFlag
                yzTypeCol={checked ? 'selected-check-button' : 'check-button'}
              >
                <Row style={{ height: '45.5px' }}>
                  <Col span={3} style={{ margin: 'auto', textIndent: '20px' }}>
                    {checked ? (
                      <img
                        alt="checked"
                        src={`${process.env.PUBLIC_URL}/img/checked-icon.webp`}
                        style={{ width: '16px', cursor: 'pointer' }}
                      />
                    ) : (
                      <img
                        alt="unchecked"
                        src={`${process.env.PUBLIC_URL}/img/unchecked-icon.webp`}
                        style={{ width: '16px', cursor: 'pointer' }}
                      />
                    )}
                  </Col>
                  <Col span={21} style={{ display: 'flex', margin: 'auto', paddingLeft: `25px` }}>
                    <TypographyText yzTypeTypographyText="basic">{label}</TypographyText>
                  </Col>
                </Row>
              </Col>
            );
          })
        }
      </Row>
      <Divider margin={42} yzTypeDivider="TRANSPARENT_LITTLE_SPACE" />
    </Layout>
  );
}

export default MainCategory;
